import { Link } from "@/lib/router-compat";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  ArrowRight,
  BadgePercent,
  ChevronLeft,
  ChevronRight,
  CircleDollarSign,
  HandCoins,
  HeartHandshake,
  ScanSearch,
  ShoppingBasket,
  Sparkles,
  Store,
  Users,
} from "lucide-react";
// LCP: usa exatamente o mesmo arquivo e a mesma tag <img> do shell crítico
// em index.html (public/embaixadora-lucia-helena-400.avif). Qualquer variante
// (picture/webp) trocaria o nó do DOM na hidratação e recontaria o LCP.
const EMBAIXADORA_AVIF = "/embaixadora-lucia-helena-400.avif";
import { proxyImage } from "@/lib/imageProxy";

// Largura máxima renderizada do banner (~520px) x2 para telas retina
import { useHomeMetrics } from "@/hooks/useGlobalStats";
import { team } from "@/content/team";


import { useEffect, useRef, useState, useCallback } from "react";

// Fallback PT-BR síncrono para o hero (LCP): evita esperar o namespace
// do i18next carregar assíncrono antes do 1º paint. Espelha
// src/i18n/locales/pt-BR/public.json → "hero".
const HERO_PT_BR = {
  badgeBrands: (count: number) => `${count} marcas parceiras`,
  badgeFallback: "Marcas parceiras",
  headlineStart: "",
  headlineHighlight: "Cashback Solidário",
  headlineEnd1: " é",
  headlineEnd2: "no Shopping do Bem",
  lead: "Doar e economizar nunca foi tão fácil.",
  subheadline:
    "Você compra pelo mesmo preço nas suas lojas favoritas. A doação acontece sozinha.",
  sinceLine: "Trazendo consciência para o processo de compra desde",
  ctaStart: "Começar a doar",
  ctaCause: "Cadastre uma causa",
  metricsTitle: "Shopping do Bem em números",
  statsGmv: "em vendas geradas para marcas",
  statsRaised: "arrecadação confirmada",
  statsMonitoredProducts: "produtos monitorados",
  statsComparableProducts: "produtos para comparar",
  statsProducts50Plus: "produtos com 50% ou mais de desconto",
  statsActiveCoupons: "cupons de desconto ativos",
  statsUsers: "Usuários",
  statsBrands: "lojas ativas",
  statsCauses: "causas ativas",
  statsVolunteers: "voluntários",
};

interface BannerItem {
  id: string;
  title?: string | null;
  image_url: string;
  image_url_mobile?: string | null;
  link_url: string | null;
  open_in_new_tab: boolean;
  bg_color: string;
  isAmbassador?: boolean;
}

const AMBASSADOR_BANNER: BannerItem = {
  id: "ambassador-lucia-helena",
  title: "Professora Lúcia Helena Galvão — Embaixadora do Shopping do Bem",
  image_url: EMBAIXADORA_AVIF,
  link_url: "https://www.instagram.com/profluciahelenagalvao",
  open_in_new_tab: true,
  bg_color: "",
  isAmbassador: true,
};

interface HeroProps {
  banners?: BannerItem[];
  bannersLoading?: boolean;
}

export const Hero = ({ banners, bannersLoading }: HeroProps) => {
  const { data: metrics } = useHomeMetrics();

  const { t, i18n } = useTranslation("public");

  // Só usa o t() do i18next quando o bundle público já está pronto E
  // o idioma não é PT-BR (evita re-render / bloqueio no caso mais comum).
  const useI18n =
    i18n.isInitialized &&
    i18n.language !== "pt-BR" &&
    i18n.hasResourceBundle(i18n.language, "public");
  const tr = (key: string, fallback: string, opts?: Record<string, unknown>) =>
    useI18n ? String(t(key, opts as never)) : fallback;

  return (
    <section className="relative overflow-hidden bg-background py-12 lg:py-14">
      <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden>
        <div className="absolute -right-48 -top-48 h-[500px] w-[500px] rounded-full bg-primary/10 blur-[120px]" />
        <div className="absolute -bottom-32 -left-32 h-[400px] w-[400px] rounded-full bg-success/10 blur-[100px]" />
      </div>

      <div className="container relative">
        <div className="grid items-start gap-8 lg:grid-cols-12 lg:gap-x-10 lg:gap-y-6">
          <div className="order-1 flex min-w-0 flex-col gap-4 text-center lg:col-span-6 lg:text-left">
            <h1 className="font-display text-[1.75rem] font-bold leading-[1.1] tracking-tight text-foreground min-[360px]:text-3xl sm:text-5xl xl:text-[3.5rem]">
              <span className="xl:block">
                {tr("hero.headlineStart", HERO_PT_BR.headlineStart)}
                <span className="text-primary">{tr("hero.headlineHighlight", HERO_PT_BR.headlineHighlight)}</span>
                {tr("hero.headlineEnd1", HERO_PT_BR.headlineEnd1)}
              </span>
              <span className="xl:block"> {tr("hero.headlineEnd2", HERO_PT_BR.headlineEnd2)}</span>
            </h1>
            <p className="mx-auto max-w-[580px] font-display text-xl font-bold leading-snug text-foreground sm:text-2xl lg:mx-0 lg:max-w-none">
              {tr("hero.lead", HERO_PT_BR.lead)}
            </p>
            <p className="mx-auto max-w-[520px] text-base leading-relaxed text-muted-foreground sm:text-lg lg:mx-0">
              {tr("hero.subheadline", HERO_PT_BR.subheadline)}
            </p>
          </div>

          <div className="order-2 relative flex min-w-0 justify-center lg:col-span-6 lg:col-start-7 lg:row-span-2 lg:row-start-1 lg:justify-start">
            {bannersLoading ? (
              <div className="h-[320px] w-full sm:h-[520px]" />
            ) : (
              <BannerMiniCarousel banners={banners && banners.length > 0 ? banners : [AMBASSADOR_BANNER]} />
            )}
          </div>

          <div className="order-3 min-w-0 lg:order-4 lg:col-span-12">
            <ProjectMetrics metrics={metrics} tr={tr} />
            <p className="mt-3 flex flex-wrap items-center justify-center gap-x-2 gap-y-1.5 text-xs font-medium text-muted-foreground sm:text-sm lg:justify-start">
              <Sparkles className="size-3.5 shrink-0 text-brand-yellow-dark" aria-hidden />
              <span>{tr("hero.sinceLine", HERO_PT_BR.sinceLine)}</span>
              <span className="rounded-full bg-brand-yellow/15 px-3 py-1 font-display text-xs font-bold tracking-wide tabular-nums text-brand-yellow-dark ring-1 ring-brand-yellow/30 sm:text-[0.8rem]">
                27/01/2025
              </span>
            </p>
          </div>

          <div className="order-4 flex flex-col justify-center gap-4 sm:flex-row lg:order-3 lg:col-span-6 lg:justify-start">
            <Button size="lg" className="group h-14 gap-3 rounded-2xl px-9 text-base font-bold shadow-xl shadow-primary/20 transition-transform hover:scale-[1.02]" asChild>
              <Link to="/cadastro">
                {tr("hero.ctaStart", HERO_PT_BR.ctaStart)}
                <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
              </Link>
            </Button>
            <Button size="lg" className="group h-14 gap-3 rounded-2xl bg-brand-green px-9 text-base font-bold text-brand-green-foreground shadow-xl shadow-brand-green/25 transition-transform hover:scale-[1.02] hover:bg-brand-green-dark" asChild>
              <Link to="/seja-parceiro">
                <HeartHandshake className="h-5 w-5" />
                {tr("hero.ctaCause", HERO_PT_BR.ctaCause)}
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

type MetricsData = ReturnType<typeof useHomeMetrics>["data"];

function compactNumber(value: number) {
  if (!Number.isFinite(value)) return "—";
  return new Intl.NumberFormat("pt-BR", { notation: "compact", maximumFractionDigits: 1 }).format(value);
}

function compactCurrency(value: number) {
  if (!Number.isFinite(value)) return "—";
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(value);
}

function fullNumber(value: number) {
  if (!Number.isFinite(value)) return "";
  return new Intl.NumberFormat("pt-BR").format(value);
}

function fullCurrency(value: number) {
  if (!Number.isFinite(value)) return "";
  return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(value);
}

function ProjectMetrics({ metrics, tr }: { metrics: MetricsData; tr: (key: string, fallback: string) => string }) {
  const items = [
    { icon: CircleDollarSign, value: metrics?.gmvConfirmedBrl != null ? compactCurrency(metrics.gmvConfirmedBrl) : "—", fullValue: metrics?.gmvConfirmedBrl != null ? fullCurrency(metrics.gmvConfirmedBrl) : "", label: tr("hero.stats.gmv", HERO_PT_BR.statsGmv), to: null },
    { icon: HandCoins, value: metrics?.approvedCommissionBrl != null ? compactCurrency(metrics.approvedCommissionBrl) : "—", fullValue: metrics?.approvedCommissionBrl != null ? fullCurrency(metrics.approvedCommissionBrl) : "", label: tr("hero.stats.raised", HERO_PT_BR.statsRaised), to: "/como-geramos" },
    { icon: ScanSearch, value: metrics?.feedProductsTotal != null ? compactNumber(metrics.feedProductsTotal) : "—", fullValue: metrics?.feedProductsTotal != null ? fullNumber(metrics.feedProductsTotal) : "", label: tr("hero.stats.monitoredProducts", HERO_PT_BR.statsMonitoredProducts), to: "/ofertas" },
    { icon: ShoppingBasket, value: metrics?.comparableProducts != null ? compactNumber(metrics.comparableProducts) : "—", fullValue: metrics?.comparableProducts != null ? fullNumber(metrics.comparableProducts) : "", label: tr("hero.stats.comparableProducts", HERO_PT_BR.statsComparableProducts), to: "/comparador-de-precos" },
    { icon: BadgePercent, value: metrics?.products50Plus != null ? compactNumber(metrics.products50Plus) : "—", fullValue: metrics?.products50Plus != null ? fullNumber(metrics.products50Plus) : "", label: tr("hero.stats.products50Plus", HERO_PT_BR.statsProducts50Plus), to: "/ofertas?sort=discount" },
    { icon: BadgePercent, value: metrics?.activeCouponCodes != null ? compactNumber(metrics.activeCouponCodes) : "—", fullValue: metrics?.activeCouponCodes != null ? fullNumber(metrics.activeCouponCodes) : "", label: tr("hero.stats.activeCoupons", HERO_PT_BR.statsActiveCoupons), to: "/cupons-de-desconto" },
    { icon: Users, value: metrics?.usersCount != null ? compactNumber(metrics.usersCount) : "—", fullValue: metrics?.usersCount != null ? fullNumber(metrics.usersCount) : "", label: tr("hero.stats.users", HERO_PT_BR.statsUsers), to: null },
    { icon: Store, value: metrics?.brandsCount != null ? compactNumber(metrics.brandsCount) : "—", fullValue: metrics?.brandsCount != null ? fullNumber(metrics.brandsCount) : "", label: tr("hero.stats.brands", HERO_PT_BR.statsBrands), to: "/marcas" },
    { icon: HeartHandshake, value: metrics?.institutionsCount != null ? compactNumber(metrics.institutionsCount) : "—", fullValue: metrics?.institutionsCount != null ? fullNumber(metrics.institutionsCount) : "", label: tr("hero.stats.causes", HERO_PT_BR.statsCauses), to: "/causas" },
    { icon: Users, value: compactNumber(team.length), fullValue: fullNumber(team.length), label: tr("hero.stats.volunteers", HERO_PT_BR.statsVolunteers), to: "/sobre-nos" },
  ];
  return (
    <section aria-labelledby="home-metrics-title" className="border-y border-border/70 py-4 sm:py-5">
      <h2 id="home-metrics-title" className="mb-3 text-center font-display text-xs font-bold uppercase tracking-widest text-muted-foreground sm:text-sm lg:text-left">
        {tr("hero.metricsTitle", HERO_PT_BR.metricsTitle)}
      </h2>
      <div className="grid grid-cols-2 gap-2 sm:gap-2.5 lg:grid-cols-5">
        {items.map(({ icon: Icon, value, fullValue, label, to }) => {
          const cardClass = `metric-card group min-w-0 rounded-xl border p-2.5 transition-all duration-200 sm:p-3${to ? " metric-card-link cursor-pointer hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" : ""}`;
          const accessibleLabel = `${fullValue} ${label}`;
          const inner = (
            <>
              <div className="mb-1.5 flex items-center gap-2">
                <span className="metric-chip grid size-7 shrink-0 place-items-center rounded-full transition-shadow duration-200" aria-hidden>
                  <Icon className="size-3.5 sm:size-4" />
                </span>
                <strong className="min-w-0 whitespace-nowrap font-display text-[0.98rem] font-bold leading-none tabular-nums text-metric-value sm:text-lg lg:text-xl">{value}</strong>
              </div>
              <p className="text-[0.68rem] font-semibold leading-snug text-muted-foreground sm:text-xs">{label}</p>
            </>
          );
          return to ? (
            <Link key={label} to={to} className={cardClass} title={accessibleLabel} aria-label={accessibleLabel}>
              {inner}
            </Link>
          ) : (
            <div key={label} className={cardClass} title={accessibleLabel}>
              {inner}
            </div>
          );
        })}
      </div>
    </section>
  );
}

function BannerMiniCarousel({ banners }: { banners: BannerItem[] }) {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const pointerStart = useRef<number | null>(null);
  const hasMultipleBanners = banners.length > 1;

  const selectPrevious = useCallback(() => {
    setSelectedIndex((current) => (current - 1 + banners.length) % banners.length);
  }, [banners.length]);

  const selectNext = useCallback(() => {
    setSelectedIndex((current) => (current + 1) % banners.length);
  }, [banners.length]);

  useEffect(() => {
    if (!hasMultipleBanners || isPaused) return;
    const interval = window.setInterval(selectNext, 7000);
    return () => window.clearInterval(interval);
  }, [hasMultipleBanners, isPaused, selectNext]);

  useEffect(() => {
    setSelectedIndex((current) => Math.min(current, Math.max(0, banners.length - 1)));
  }, [banners.length]);

  const placementFor = (index: number) => {
    if (index === selectedIndex) return "active";
    if (banners.length === 2) return "next";
    if (index === (selectedIndex - 1 + banners.length) % banners.length) return "previous";
    if (index === (selectedIndex + 1) % banners.length) return "next";
    return "hidden";
  };

  const cardClasses = {
    active: "z-30 translate-x-0 rotate-0 scale-100 opacity-100 rounded-[2rem] border-[6px]",
    next: "z-20 translate-x-[9%] rotate-3 scale-[0.84] opacity-85 brightness-90 rounded-3xl border-4 sm:translate-x-[16%] sm:rotate-6",
    previous: "z-20 -translate-x-[9%] -rotate-3 scale-[0.84] opacity-85 brightness-90 rounded-3xl border-4 sm:-translate-x-[16%] sm:-rotate-6",
    hidden: "pointer-events-none z-0 scale-75 opacity-0 rounded-3xl border-4",
  } as const;


  const handlePointerUp = (event: React.PointerEvent<HTMLDivElement>) => {
    if (pointerStart.current === null) return;
    const distance = event.clientX - pointerStart.current;
    pointerStart.current = null;
    if (Math.abs(distance) < 45) return;
    if (distance < 0) selectNext();
    else selectPrevious();
  };

  return (
    <div
      className="group/carousel relative flex w-full touch-pan-y select-none flex-col items-center"
      role="region"
      aria-label={hasMultipleBanners ? "Banners em destaque" : banners[0]?.title ?? "Destaque"}
      aria-roledescription={hasMultipleBanners ? "carrossel" : undefined}
      tabIndex={hasMultipleBanners ? 0 : undefined}
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      onFocusCapture={() => setIsPaused(true)}
      onBlurCapture={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) setIsPaused(false);
      }}
      onKeyDown={(event) => {
        if (!hasMultipleBanners) return;
        if (event.key === "ArrowLeft") selectPrevious();
        if (event.key === "ArrowRight") selectNext();
      }}
      onPointerDown={hasMultipleBanners ? (event) => { pointerStart.current = event.clientX; } : undefined}
      onPointerUp={hasMultipleBanners ? handlePointerUp : undefined}
      onPointerCancel={hasMultipleBanners ? () => { pointerStart.current = null; } : undefined}
    >
      <div className="relative w-full">
        <div className="relative mx-auto w-full aspect-[912/800] sm:aspect-[1200/704]">

        {banners.map((banner, bi) => {
          const placement = placementFor(bi);
          const isActive = placement === "active";
          const Wrapper = banner.link_url ? "a" : "div";
          const linkProps = banner.link_url && isActive
            ? {
                href: banner.link_url,
                "aria-label": banner.title || "Banner promocional",
                target: banner.open_in_new_tab ? "_blank" as const : "_self" as const,
                rel: banner.open_in_new_tab ? "noopener noreferrer" : undefined,
              }
            : {};
          return (
            <Wrapper
              key={banner.id}
              {...linkProps}
              aria-hidden={!isActive}
              className={`banner-metal-frame absolute inset-y-0 left-[11%] right-[11%] overflow-hidden bg-card transition-all duration-[600ms] ease-out motion-reduce:duration-0 ${cardClasses[placement]} ${isActive && banner.link_url ? "cursor-pointer" : ""}`}
              style={{ backgroundColor: banner.bg_color }}
            >
              {/* Imagem preenche todo o cartão; mobile recebe a versão própria quando existe */}
              <picture className="absolute inset-0 h-full w-full">
                {!banner.isAmbassador && banner.image_url_mobile && (
                  <source media="(max-width: 767px)" srcSet={banner.image_url_mobile} />
                )}
                <img
                  src={banner.isAmbassador ? banner.image_url : proxyImage(banner.image_url, { w: 1200, h: 704, q: 75 }) || banner.image_url}
                  alt={banner.isAmbassador ? "Professora Lúcia Helena Galvão - Embaixadora do Shopping do Bem" : ""}
                  width={banner.isAmbassador ? 400 : 1200}
                  height={banner.isAmbassador ? 500 : 704}
                  fetchPriority={isActive ? "high" : "auto"}
                  loading={isActive ? "eager" : "lazy"}
                  decoding={banner.isAmbassador ? "sync" : "async"}
                  className={`h-full w-full ${banner.isAmbassador ? "object-cover" : "object-fill"}`}
                />
              </picture>
            </Wrapper>
          );
        })}

        </div>

        {hasMultipleBanners && (
          <>
            <Button type="button" size="icon" variant="outline" onClick={selectPrevious} aria-label="Banner anterior"
              className="absolute left-0 top-1/2 z-40 size-8 -translate-y-1/2 rounded-full border border-border/70 bg-card/95 shadow-md transition-transform hover:scale-105 sm:-left-2 sm:size-10">
              <ChevronLeft className="size-5" />
            </Button>
            <Button type="button" size="icon" variant="outline" onClick={selectNext} aria-label="Próximo banner"
              className="absolute right-0 top-1/2 z-40 size-8 -translate-y-1/2 rounded-full border border-border/70 bg-card/95 shadow-md transition-transform hover:scale-105 sm:-right-2 sm:size-10">

              <ChevronRight className="size-5" />
            </Button>
          </>
        )}
      </div>

      {hasMultipleBanners && (
        <div className="mt-4 flex justify-center gap-1.5" aria-label="Escolher banner">
          {banners.map((_, i) => (
            <button key={i} type="button" onClick={() => setSelectedIndex(i)} aria-label={`Ir para o banner ${i + 1}`}
              aria-current={i === selectedIndex ? "true" : undefined} className="grid h-6 place-items-center px-0.5">
              <span className={`block h-2 rounded-full transition-all ${i === selectedIndex ? "w-8 bg-primary" : "w-2 bg-muted-foreground/25"}`} />
            </button>
          ))}
        </div>
      )}
    </div>

  );
}
